/**
 * Is the server reachable from this tablet?
 *
 * `navigator.onLine` alone is not the answer: a tablet on the restaurant's
 * Wi-Fi is "online" when the router has lost the internet. So the browser's
 * word is taken when it says offline, and otherwise the server's `/health` is
 * asked every so often — every 20 seconds while offline, to notice the line
 * coming back; every minute while online, to notice it going.
 */

import { useEffect, useState } from 'react';

export type Connection = 'online' | 'offline';

async function reachable(): Promise<boolean> {
  if (!navigator.onLine) return false;
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), 5_000);
  try {
    const response = await fetch('/health', { cache: 'no-store', signal: controller.signal });
    return response.ok;
  } catch {
    return false;
  } finally {
    window.clearTimeout(timer);
  }
}

export function useConnection(): Connection {
  const [state, setState] = useState<Connection>(navigator.onLine ? 'online' : 'offline');

  useEffect(() => {
    let alive = true;
    let timer: number | undefined;

    const check = async () => {
      const ok = await reachable();
      if (!alive) return;
      setState(ok ? 'online' : 'offline');
      timer = window.setTimeout(() => void check(), ok ? 60_000 : 20_000);
    };
    const now = () => {
      window.clearTimeout(timer);
      void check();
    };

    void check();
    window.addEventListener('online', now);
    window.addEventListener('offline', now);
    return () => {
      alive = false;
      window.clearTimeout(timer);
      window.removeEventListener('online', now);
      window.removeEventListener('offline', now);
    };
  }, []);

  return state;
}
